import React, { useState, useEffect, useRef } from "react";
import { FieldShell, SelectField, TextInputField, UploadBox, PrimaryButton } from "./components";
import CreatableCombobox from "../../../../components/CreatableCombobox";
import { useToast } from "../../../../hooks/useToast";
import providersService from "../../../../api/services/providersService";
import { validateUploadFile } from "../../../../utils/directUpload";
import { ACCEPTED_IMAGE_ACCEPT } from "../../../../constants/imageUpload";
import {
  VEHICLE_BRANDS,
  VEHICLE_BRAND_MAX_LENGTH,
  VEHICLE_TYPES,
  VEHICLE_YEARS,
} from "../../../../constants/vehicle";


const parsePositiveNumber = (value) => {
  const cleaned = String(value ?? "").replace(/[^\d.]/g, "");
  if (!cleaned) return undefined;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const getFilenameFromUrl = (value = "") => {
  if (!value || typeof value !== "string") return "";
  const trimmed = value.split("?")[0];
  const parts = trimmed.split("/");
  return parts[parts.length - 1] || "";
};

const resolveExistingName = (source) => {
  if (!source) return "";
  if (typeof source === "string") return getFilenameFromUrl(source) || source;
  if (source.file) return resolveExistingName(source.file);
  return source.name || getFilenameFromUrl(source.url || source.file?.url || "");
};

const unwrapVehicle = (response) => {
  if (!response) return {};
  return (
    response.data?.data?.vehicle ||
    response.data?.vehicle ||
    response.data?.data ||
    response.data ||
    response.vehicle ||
    response
  );
};

export default function VehiclePanel({ profile, onRequestReview }) {
  const toast = useToast();
  const vehicleDocInputRef = useRef(null);
  const vehicleImageInputRef = useRef(null);

  const [vehicleForm, setVehicleForm] = useState({
    type: "",
    capacity: "",
    brand: "",
    year: "",
  });
  const [vehicleFiles, setVehicleFiles] = useState({
    documents: null,
    images: null,
  });
  const [vehicleFileNames, setVehicleFileNames] = useState({
    documents: "",
    images: "",
  });
  const [existingVehicleFileNames, setExistingVehicleFileNames] = useState({
    documents: "",
    images: "",
  });
  const [isSavingVehicle, setIsSavingVehicle] = useState(false);

  useEffect(() => {
    if (profile) {
      const vehicleData = profile.vehicle || {};
      const resolvedVehicleType =
        vehicleData.type || vehicleData.vehicleType || vehicleData.transportType || "";
      const resolvedVehicleBrand = vehicleData.brand || vehicleData.make || "";
      const resolvedVehicleCapacity =
        vehicleData.maxPassengers ??
        vehicleData.max_passengers ??
        vehicleData.capacity ??
        vehicleData.maxPassenger ??
        "";
      const resolvedVehicleYear =
        vehicleData.year || vehicleData.vehicleYear || vehicleData.vehicle_year || "";

      setVehicleForm({
        type: resolvedVehicleType,
        capacity: resolvedVehicleCapacity !== "" ? String(resolvedVehicleCapacity) : "",
        brand: resolvedVehicleBrand,
        year: resolvedVehicleYear !== "" ? String(resolvedVehicleYear) : "",
      });

      const vehicleDocumentSources = Array.isArray(vehicleData.vehicleDocuments)
        ? vehicleData.vehicleDocuments
        : Array.isArray(vehicleData.documents)
          ? vehicleData.documents
          : Array.isArray(vehicleData.vehicle_document)
            ? vehicleData.vehicle_document
            : [];
      const vehicleImageSources = Array.isArray(vehicleData.vehicleImages)
        ? vehicleData.vehicleImages
        : Array.isArray(vehicleData.images)
          ? vehicleData.images
          : Array.isArray(vehicleData.vehicle_images)
            ? vehicleData.vehicle_images
            : [];

      setExistingVehicleFileNames({
        documents: resolveExistingName(vehicleDocumentSources[0]),
        images: resolveExistingName(vehicleImageSources[0]),
      });
    }
  }, [profile]);

  const handleVehicleFileChange = (key, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    // Images only — the API has always rejected PDFs, so accepting them here
    // only produced a failure after the upload.
    const validationError = validateUploadFile(file);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setVehicleFiles((prev) => ({ ...prev, [key]: file }));
    setVehicleFileNames((prev) => ({ ...prev, [key]: file.name || "" }));
  };

  const validateVehicleForm = () => {
    const parsedCapacity = parsePositiveNumber(vehicleForm.capacity);
    const parsedYear = parsePositiveNumber(vehicleForm.year);

    if (!vehicleForm.type || !vehicleForm.brand || !parsedCapacity || !parsedYear) {
      toast.error("Please complete all vehicle fields.");
      return false;
    }
    if (!vehicleFiles.documents && !existingVehicleFileNames.documents) {
      toast.error("Please upload vehicle documents.");
      return false;
    }
    if (!vehicleFiles.images && !existingVehicleFileNames.images) {
      toast.error("Please upload vehicle images.");
      return false;
    }

    return true;
  };

  const handleSaveClick = () => {
    if (!validateVehicleForm()) return;

    onRequestReview("vehicle", async () => {
      setIsSavingVehicle(true);
      try {
        const parsedCapacity = parsePositiveNumber(vehicleForm.capacity);
        const parsedYear = parsePositiveNumber(vehicleForm.year);

        await providersService.updateVehicle({
          type: vehicleForm.type,
          maxPassengers: parsedCapacity,
          brand: vehicleForm.brand,
          year: parsedYear,
          vehicleDocuments: vehicleFiles.documents ? [vehicleFiles.documents] : [],
          vehicleImages: vehicleFiles.images ? [vehicleFiles.images] : [],
        });

        const refreshedVehicle = unwrapVehicle(await providersService.getVehicle());
        const refreshedDocuments = Array.isArray(refreshedVehicle.vehicleDocuments)
          ? refreshedVehicle.vehicleDocuments
          : Array.isArray(refreshedVehicle.documents)
            ? refreshedVehicle.documents
            : [];
        const refreshedImages = Array.isArray(refreshedVehicle.vehicleImages)
          ? refreshedVehicle.vehicleImages
          : Array.isArray(refreshedVehicle.images)
            ? refreshedVehicle.images
            : [];

        setExistingVehicleFileNames({
          documents: resolveExistingName(refreshedDocuments[0]) || vehicleFileNames.documents,
          images: resolveExistingName(refreshedImages[0]) || vehicleFileNames.images,
        });
        setVehicleFiles({ documents: null, images: null });
        setVehicleFileNames({ documents: "", images: "" });
        toast.success("Vehicle information updated successfully.");
        return true;
      } catch (error) {
        toast.apiError(error, "Unable to update vehicle information.");
        return false;
      } finally {
        setIsSavingVehicle(false);
      }
    });
  };

  return (
    <div className="space-y-5">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <SelectField
          label="Vehicle Type"
          value={vehicleForm.type}
          onChange={(value) => {
            setVehicleForm((prev) => ({ ...prev, type: value }));
          }}
          options={VEHICLE_TYPES}
          placeholder="Select Vehicle Type"
        />
        <TextInputField
          label="Maximum Passenger Capacity"
          value={vehicleForm.capacity}
          onChange={(value) => {
            setVehicleForm((prev) => ({ ...prev, capacity: value }));
          }}
          placeholder="e.g. 4"
          type="number"
          inputMode="numeric"
        />
        <FieldShell label="Vehicle Brand">
          <CreatableCombobox
            value={vehicleForm.brand}
            onChange={(value) => {
              setVehicleForm((prev) => ({ ...prev, brand: value }));
            }}
            options={VEHICLE_BRANDS}
            placeholder="Select or type vehicle brand"
            maxLength={VEHICLE_BRAND_MAX_LENGTH}
            inputClassName="!rounded-xl"
          />
        </FieldShell>
        <SelectField
          label="Vehicle Year"
          value={vehicleForm.year}
          onChange={(value) => {
            setVehicleForm((prev) => ({ ...prev, year: value }));
          }}
          options={VEHICLE_YEARS}
          placeholder="Select Vehicle Year"
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <UploadBox
          title="Upload Vehicle Documents"
          helper="Permit, state papers, and other relevant documents."
          fileName={vehicleFileNames.documents || existingVehicleFileNames.documents}
          buttonLabel="Choose File"
          onClick={() => vehicleDocInputRef.current?.click()}
        >
          <input
            ref={vehicleDocInputRef}
            type="file"
            accept={ACCEPTED_IMAGE_ACCEPT}
            className="hidden"
            onChange={(event) => handleVehicleFileChange("documents", event)}
          />
        </UploadBox>

        <UploadBox
          title="Vehicle Images"
          helper="Drop clear photos of your vehicle. PNG or JPG only."
          fileName={vehicleFileNames.images || existingVehicleFileNames.images}
          buttonLabel="Choose File"
          onClick={() => vehicleImageInputRef.current?.click()}
        >
          <input
            ref={vehicleImageInputRef}
            type="file"
            accept={ACCEPTED_IMAGE_ACCEPT}
            className="hidden"
            onChange={(event) => handleVehicleFileChange("images", event)}
          />
        </UploadBox>
      </div>

      <div className="flex justify-end pt-4">
        <PrimaryButton
          onClick={handleSaveClick}
          disabled={isSavingVehicle}
          label={isSavingVehicle ? "Saving..." : "Save Changes"}
        />
      </div>
    </div>
  );
}
